import { useCallback, useState } from "react";
import type { CurrentUser } from "@spf/contracts";
import { viewsFor, type ViewDefinition, type ViewId } from "../lib/views.js";
import { MobileNavigation, Navigation } from "./Navigation.js";
import { HeaderActions } from "./HeaderActions.js";
import { AccountMenu } from "./AccountMenu.js";
import { BrandMark } from "./BrandMark.js";
import { CustomizerPanel } from "./CustomizerPanel.js";
import { SearchModal } from "./modals/SearchModal.js";
import { NotificationsModal } from "./modals/NotificationsModal.js";
import { SettingsModal } from "./modals/SettingsModal.js";
import { usePreferences } from "../lib/preferences.js";
import { isPlainLeftClick } from "../lib/useRoute.js";
import { cn } from "../lib/cn.js";

/** Which dialog the chrome has open, if any. */
export type ShellModal = "search" | "notifications" | "settings" | null;

/**
 * Which of the header's two dropdowns is showing.
 *
 * One piece of state rather than one per component, because both are
 * `absolute` from the same header and their boxes overlap: the sections drawer
 * spans the full width below the header and the account menu hangs under the
 * avatar inside it. Owned separately they could both be open at once, and the
 * drawer then sat on top of the menu and swallowed every click meant for it.
 */
type Popover = "sections" | "account" | null;

interface Props {
  user: CurrentUser;
  view: ViewId;
  onNavigate: (path: string) => void;
  onSignOut: () => void;
  children: React.ReactNode;
}

/**
 * The frame every signed-in view sits in: the design's header, one row.
 *
 * Brand at the left, the pill rail in the middle, the action icons at the
 * right -- customise, theme, search, notifications, settings, avatar -- which
 * is the design's arrangement, unchanged. Before the port this was two rows
 * with ten pills wrapping across the second, which is why the rail now holds
 * the design's five and the rest hang off the avatar (`lib/views.ts`).
 *
 * Two things here are contracts rather than choices, both asserted by the
 * browser suite at all four widths: the signed-in address is rendered as
 * visible text exactly once, and the sign-out control is named exactly
 * "Sign out". Both now live in the account menu, and the suite opens it.
 */
export function AppShell({ user, view, onNavigate, onSignOut, children }: Props) {
  const views = viewsFor(user);
  const primary = views.filter((v) => v.group === "primary");
  const account = views.filter((v) => v.group === "account");
  const { density } = usePreferences();

  const [customizing, setCustomizing] = useState(false);
  const [modal, setModal] = useState<ShellModal>(null);
  const [popover, setPopover] = useState<Popover>(null);
  const showAccount = useCallback((open: boolean) => {
    setPopover(open ? "account" : null);
  }, []);
  const showSections = useCallback((open: boolean) => {
    setPopover(open ? "sections" : null);
  }, []);
  const closeModal = useCallback(() => {
    setModal(null);
  }, []);

  return (
    <div className="min-h-dvh bg-page">
      {/* A keyboard reader should not have to walk the whole navigation on
          every page. The design had no skip link. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-full focus:bg-card focus:px-4 focus:py-2 focus:text-ink"
      >
        Skip to content
      </a>

      {/*
        Opaque, where the design leaves the header transparent over the page.

        A translucent or transparent sticky header puts the navigation on a
        background that is part page colour and part whatever happens to be
        scrolled underneath, and the contrast of every link then depends on the
        content behind it. axe measured the blend at 3.61:1 for an inactive
        link and 4.21:1 for the active pill, against the 4.5:1 section 4
        requires -- while the token values themselves pass comfortably. It also
        made the gate unreliable rather than merely slow: the same checks
        passed on retry whenever different content sat under the header, so a
        real violation was being recorded as flakiness.
      */}
      <header className="sticky top-0 z-30 bg-page">
        <div className="relative mx-auto flex max-w-[100rem] items-center justify-between gap-3 px-4 pb-3 pt-4 sm:px-6 lg:px-8">
          <Brand onNavigate={onNavigate} landing={primary[0]} />

          {primary.length > 1 && (
            <Navigation views={primary} current={view} onNavigate={onNavigate} />
          )}

          <div className="flex items-center gap-2 sm:gap-2.5">
            <HeaderActions
              customizing={customizing}
              onCustomize={() => {
                setCustomizing((v) => !v);
              }}
              onOpen={setModal}
            />
            <AccountMenu
              email={user.email}
              views={account}
              current={view}
              onNavigate={onNavigate}
              onSignOut={onSignOut}
              open={popover === "account"}
              onOpenChange={showAccount}
            />
            {primary.length > 1 && (
              <MobileNavigation
                views={primary}
                current={view}
                onNavigate={onNavigate}
                open={popover === "sections"}
                onOpenChange={showSections}
              />
            )}
          </div>
        </div>
      </header>

      {customizing && (
        <CustomizerPanel
          onDone={() => {
            setCustomizing(false);
          }}
        />
      )}

      <main
        id="main"
        className={cn(
          "mx-auto max-w-[100rem] px-4 sm:px-6 lg:px-8",
          density === "compact" ? "space-y-3 py-4" : "space-y-5 py-6 sm:py-8",
        )}
      >
        {children}
      </main>

      {modal === "search" && (
        <SearchModal
          views={views}
          onClose={closeModal}
          onNavigate={onNavigate}
          onCustomize={() => {
            setCustomizing(true);
          }}
        />
      )}
      {modal === "notifications" && <NotificationsModal onClose={closeModal} />}
      {modal === "settings" && (
        <SettingsModal
          canWrite={user.permissions.includes("settings:write")}
          onClose={closeModal}
        />
      )}
    </div>
  );
}

/**
 * The mark and the wordmark. A link, where the design used a `div onClick`
 * with no keyboard path at all.
 *
 * The wordmark stays "Solar Dashboard" and stays an `<h1>`: the design's own
 * brand is not this product's, and the heading is what the browser suite uses
 * to tell the shell apart from the Dashboard view's own heading.
 */
function Brand({
  onNavigate,
  landing,
}: {
  onNavigate: (path: string) => void;
  landing: ViewDefinition | undefined;
}) {
  const content = (
    <>
      <BrandMark className="h-8 w-8 shrink-0 transition-transform duration-300 group-hover:scale-105" />
      {/* Hidden below `sm`, not removed. At 360 the wordmark, the action rail
          and the menu do not fit on one row, and the wordmark was the thing
          that gave -- it truncated to nothing, leaving the page's only `<h1>`
          rendered as an empty box. `sr-only` takes it out of the layout and
          leaves it in the accessibility tree, so the heading is still there
          for anyone reading by structure and the mark carries the brand for
          anyone reading by eye. */}
      <h1 className="truncate text-lg font-bold tracking-tight text-ink max-sm:sr-only sm:text-2xl">
        Solar Dashboard
      </h1>
    </>
  );

  if (landing === undefined) {
    return <div className="flex min-w-0 items-center gap-3">{content}</div>;
  }

  return (
    <a
      href={landing.path}
      onClick={(event) => {
        if (!isPlainLeftClick(event)) return;
        event.preventDefault();
        onNavigate(landing.path);
      }}
      className="group flex min-w-0 select-none items-center gap-3"
    >
      {content}
    </a>
  );
}
